import posthog from 'posthog-js';

import { UserRight } from '@amzn/coral_com-amazon-asxcustomerprofileservice';

import { logger } from 'src/logger';
import { swrFetcher } from 'src/utils/swr-helpers';

// Project tokens are used to decide which project within PostHog to place the event into.
// These are exposed to the browser and are not secrets.
export const getStage = (): string => 'prod'.toLowerCase();

export const getPostHogConfig = (stage: string) => {
  const PUBLIC_POSTHOG_PROJECT_TOKEN =
    stage === 'prod'
      ? 'phc_8w5OtJQxITg2qZOfx9w4hZrdZVOlc1yXBMVlRbdeYJW'
      : 'phc_9t2NaA4EuvBc1KeCiGta3WhpXEHSqlnOVlmQ2f03zMR';

  return {
    token: PUBLIC_POSTHOG_PROJECT_TOKEN,
    host: '/tricorder',
  };
};

/**
 * Checks user authentication status from the ACPS getUserRights endpoint
 * Returns whether the user is authenticated
 * Uses the same fetcher pattern as the useGetUserRights hook for consistency
 * First checks sessionStorage for cached rights to avoid duplicate API calls
 */
export const checkUserAuthentication = async (): Promise<boolean> => {
  try {
    // Check sessionStorage first for cached user rights (set by useGetUserRights hook)
    const cachedRights = sessionStorage.getItem('userRights');
    if (cachedRights) {
      return cachedRights.includes(UserRight.AUTHENTICATED);
    }

    // If no cached rights, fetch from API
    const data = await swrFetcher('/acps/getUserRights');

    // Check if user is authenticated
    const userRights = data?.userRights || [];
    return userRights.includes(UserRight.AUTHENTICATED);
  } catch (error) {
    logger.error('Failed to check user authentication for Tricorder', error as Error);
    return false;
  }
};

/**
 * Fetches user information from the ACPS getPrimaryContactInformation endpoint
 * Returns the user's obfuscated customer ID for PostHog identification (privacy-safe)
 * Only called when user is authenticated
 * Uses the same fetcher pattern as the useGetPrimaryContactInformation hook for consistency
 */
export const getUser = async (): Promise<{ user?: string } | null> => {
  try {
    const data = await swrFetcher('/acps/getPrimaryContactInformation');

    // Return null if data is null/undefined or if obfuscatedCustomerId is not available
    if (!data || !data.obfuscatedCustomerId) {
      return null;
    }

    // Use obfuscatedCustomerId as the user identifier for PostHog (privacy-safe)
    return { user: data.obfuscatedCustomerId };
  } catch (error) {
    logger.error('Failed to fetch user information for Tricorder', error as Error);
    return null;
  }
};

export const initTricorder = async (stage: string = getStage()) => {
  if (stage === 'gamma') {
    // Tricorder only has beta / prod setup completed, potentially resulting in browser errors in gamma.
    // We will skip gamma metrics for stability and reducing error noisiness.
    return;
  }

  const config = getPostHogConfig(stage);

  try {
    // Always initialize PostHog, regardless of user authentication status
    posthog.init(config.token, {
      api_host: config.host,
    });

    // Check user authentication status first
    const isAuthenticated = await checkUserAuthentication();

    if (isAuthenticated) {
      // User is authenticated, try to get user information for identification
      const userObj = await getUser();
      const userAlias = userObj?.user;

      if (userAlias) {
        // If user is logged in and we got their info, identify them with their obfuscated customer ID
        posthog.identify(userAlias);
        logger.info('Tricorder initialized with user identification', { userAlias });
      } else {
        // User is authenticated but we couldn't get their info, still track as anonymous
        logger.info('Tricorder initialized for authenticated user (anonymous session due to missing user info)');
      }
    } else {
      // User is not authenticated (UNAUTHENTICATED), create anonymous session without calling primary contact API
      logger.info('Tricorder initialized for unauthenticated user (anonymous session)');
    }
  } catch (e) {
    logger.warn('Error initializing Posthog', e as Error);
  }
};
